import React, { useId } from 'react';

// Brand palette from The Sport Bloc design system
export const B = {
  green:   '#0e4d3a',
  greenDk: '#082a20',
  cream:   '#f3f1e8',
  paper:   '#faf8f1',
  ink:     '#18181a',
  lime:    '#c8e94d',
  muted:   '#8a8a82',
};

/**
 * SB Monogram Seal — primary brand mark (Concept 01).
 * A circular badge with double-ring, "SB" monogram, and curved brand text.
 *
 * Props:
 *   size  — diameter in px (default 240)
 *   fill  — badge fill color (default deep green)
 *   ink   — stroke/text color (default cream)
 */
export const SealSB = ({ size = 240, fill = B.green, ink = B.cream }) => {
  const uid = useId().replace(/:/g, '');
  const arcTopId = `${uid}-arc-top`;
  const arcBotId = `${uid}-arc-bot`;

  return (
    <svg viewBox="0 0 240 240" width={size} height={size} style={{ flexShrink: 0 }}>
      <defs>
        <path id={arcTopId} d="M 30,120 a 90,90 0 0 1 180,0" fill="none"/>
        <path id={arcBotId} d="M 30,120 a 90,90 0 0 0 180,0" fill="none"/>
      </defs>
      {/* outer ring */}
      <circle cx="120" cy="120" r="116" fill={fill}/>
      <circle cx="120" cy="120" r="116" fill="none" stroke={ink} strokeWidth="3"/>
      <circle cx="120" cy="120" r="104" fill="none" stroke={ink} strokeWidth="2"/>
      {/* inner disc */}
      <circle cx="120" cy="120" r="78" fill={ink}/>
      {/* monogram SB */}
      <text x="120" y="148" textAnchor="middle"
            fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="92"
            fill={fill} style={{ letterSpacing: '-0.04em', fontStretch: 'condensed' }}>
        SB
      </text>
      {/* curved top text — THE SPORT BLOC */}
      <text fill={ink} fontFamily="Archivo Narrow, Archivo, sans-serif"
            fontWeight="700" fontSize="12" letterSpacing="0.28em">
        <textPath href={`#${arcTopId}`} startOffset="50%" textAnchor="middle">
          THE SPORT BLOC
        </textPath>
      </text>
      {/* curved bottom text — EST · SHILLONG */}
      <text fill={ink} fontFamily="JetBrains Mono, monospace"
            fontWeight="500" fontSize="9.5" letterSpacing="0.4em">
        <textPath href={`#${arcBotId}`} startOffset="50%" textAnchor="middle">
          ★ EST · SHILLONG · 2024 ★
        </textPath>
      </text>
      {/* side pegs */}
      <circle cx="6" cy="120" r="5" fill={fill} stroke={ink} strokeWidth="2"/>
      <circle cx="234" cy="120" r="5" fill={fill} stroke={ink} strokeWidth="2"/>
    </svg>
  );
};

/**
 * Horizontal lockup: seal icon + wordmark text.
 * sealSize — seal diameter (default 44)
 * dark     — cream/lime text for dark backgrounds (default true)
 */
export const SportBlocLockup = ({ sealSize = 44, dark = true, className = '' }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <SealSB size={sealSize} />
    <div style={{ lineHeight: 1 }}>
      <div style={{
        fontFamily: 'Archivo Narrow, Archivo, sans-serif',
        fontWeight: 800,
        fontSize: sealSize * 0.38,
        letterSpacing: '0.16em',
        color: dark ? B.cream : B.green,
        lineHeight: 1.05,
      }}>
        THE SPORT
      </div>
      <div style={{
        fontFamily: 'Archivo Narrow, Archivo, sans-serif',
        fontWeight: 900,
        fontSize: sealSize * 0.42,
        letterSpacing: '0.22em',
        color: B.lime,
        lineHeight: 1,
      }}>
        BLOC
      </div>
    </div>
  </div>
);

/**
 * Football sub-brand seal — circular badge with football icon.
 */
export const FootballSubMark = ({ size = 200 }) => {
  const uid = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} style={{ flexShrink: 0 }}>
      <defs>
        <path id={`${uid}-fb-top`} d="M 30,120 a 90,90 0 0 1 180,0" fill="none"/>
        <path id={`${uid}-fb-bot`} d="M 30,120 a 90,90 0 0 0 180,0" fill="none"/>
      </defs>
      <circle cx="120" cy="120" r="116" fill={B.green}/>
      <circle cx="120" cy="120" r="116" fill="none" stroke={B.cream} strokeWidth="3"/>
      <circle cx="120" cy="120" r="104" fill="none" stroke={B.cream} strokeWidth="2"/>
      <g transform="translate(120 122)">
        <circle r="70" fill={B.cream}/>
        <polygon points="0,-30 25,-9 16,21 -16,21 -25,-9" fill={B.green}/>
        <line x1="0" y1="-30" x2="0" y2="-58" stroke={B.green} strokeWidth="4"/>
        <line x1="25" y1="-9" x2="50" y2="-19" stroke={B.green} strokeWidth="4"/>
        <line x1="-25" y1="-9" x2="-50" y2="-19" stroke={B.green} strokeWidth="4"/>
        <line x1="16" y1="21" x2="28" y2="49" stroke={B.green} strokeWidth="4"/>
        <line x1="-16" y1="21" x2="-28" y2="49" stroke={B.green} strokeWidth="4"/>
      </g>
      <text fill={B.cream} fontFamily="Archivo Narrow, Archivo, sans-serif"
            fontWeight="800" fontSize="14" letterSpacing="0.28em">
        <textPath href={`#${uid}-fb-top`} startOffset="50%" textAnchor="middle">FOOTBALL</textPath>
      </text>
      <text fill={B.cream} fontFamily="Archivo Narrow, Archivo, sans-serif"
            fontWeight="800" fontSize="13" letterSpacing="0.26em">
        <textPath href={`#${uid}-fb-bot`} startOffset="50%" textAnchor="middle">SPORT ARENA</textPath>
      </text>
      <circle cx="6" cy="120" r="5" fill={B.green} stroke={B.cream} strokeWidth="2"/>
      <circle cx="234" cy="120" r="5" fill={B.green} stroke={B.cream} strokeWidth="2"/>
    </svg>
  );
};

/**
 * Racquet (Pickleball) sub-brand seal — circular badge with pickleball icon.
 */
export const RacquetSubMark = ({ size = 200 }) => {
  const uid = useId().replace(/:/g, '');
  const holes = [];
  const R = 58, hR = 4.5, step = 13;
  for (let row = -4; row <= 4; row++) {
    const y = row * step * 0.92;
    const xoff = row % 2 === 0 ? 0 : step / 2;
    for (let col = -4; col <= 4; col++) {
      const x = col * step + xoff;
      if (x * x + y * y < (R - 8) * (R - 8)) holes.push({ x, y });
    }
  }
  return (
    <svg viewBox="0 0 240 240" width={size} height={size} style={{ flexShrink: 0 }}>
      <defs>
        <path id={`${uid}-rq-top`} d="M 30,120 a 90,90 0 0 1 180,0" fill="none"/>
        <path id={`${uid}-rq-bot`} d="M 30,120 a 90,90 0 0 0 180,0" fill="none"/>
      </defs>
      <circle cx="120" cy="120" r="116" fill={B.green}/>
      <circle cx="120" cy="120" r="116" fill="none" stroke={B.cream} strokeWidth="3"/>
      <circle cx="120" cy="120" r="104" fill="none" stroke={B.cream} strokeWidth="2"/>
      <g transform="translate(120 122)">
        <circle r={R} fill={B.lime} stroke={B.cream} strokeWidth="4"/>
        <path d={`M ${-R + 8} -18 A ${R - 6} ${R - 6} 0 0 1 -18 ${-R + 8}`}
              fill="none" stroke={B.cream} strokeWidth="2" strokeOpacity="0.45" strokeLinecap="round"/>
        {holes.map((h, i) => (
          <circle key={i} cx={h.x} cy={h.y} r={hR} fill={B.green} stroke={B.cream} strokeWidth="0.8"/>
        ))}
      </g>
      <text fill={B.cream} fontFamily="Archivo Narrow, Archivo, sans-serif"
            fontWeight="800" fontSize="14" letterSpacing="0.28em">
        <textPath href={`#${uid}-rq-top`} startOffset="50%" textAnchor="middle">RACQUET</textPath>
      </text>
      <text fill={B.cream} fontFamily="Archivo Narrow, Archivo, sans-serif"
            fontWeight="800" fontSize="13" letterSpacing="0.26em">
        <textPath href={`#${uid}-rq-bot`} startOffset="50%" textAnchor="middle">SPORT ARENA</textPath>
      </text>
      <circle cx="6" cy="120" r="5" fill={B.green} stroke={B.cream} strokeWidth="2"/>
      <circle cx="234" cy="120" r="5" fill={B.green} stroke={B.cream} strokeWidth="2"/>
    </svg>
  );
};

export default SealSB;
